import { t, Trans } from '@lingui/macro';
import { Player } from "@lottiefiles/react-lottie-player";
import { AlertCircle, ArrowLeft, XCircle } from 'lucide-react';
import { useEffect, useState } from "react";
import { useHistory, useParams } from "../../hooks/useRouterCompat";
import { useAppUIContext } from "../../context/AppUIContext";
import { JoinRoomViaInviteResponse } from "../../models/DTO";
import { Room } from "../../models/Room";
import { RoomInvite } from "../../models/RoomInvite";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import ProfileImg from "../components/ProfileImg";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardHeader, CardContent, CardFooter } from "../../components/ui/card";
import { Avatar } from "../../components/ui/avatar";
import { Progress } from "../../components/ui/progress";
import { Separator } from "../../components/ui/separator";

const RoomInvitationPage: React.FC = () => {
  const history = useHistory();
  const { setActiveWorkspaceId } = useAppUIContext();
  const [inProgress, setInProgress] = useState(false);
  const [invite, setInvite] = useState<RoomInvite | null>(null);
  const [room, setRoom] = useState<Room>();
  const [isMobileBrowser, setIsMobileBrowser] = useState(false);
  const { invitationId } = useParams<{ invitationId: string }>();
  const roomImage = invite?.roomImageUrl ?? room?.profileImageUrl;
  const roomName = invite?.roomName ?? room?.name;
  const roomDescription = invite?.roomDescription ?? room?.description;
  const [passwordError, setPasswordError] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);
  // 도메인 제한 워크스페이스 사전 안내 (서버 강제는 room 트리거가 담당)
  const [joinRestriction, setJoinRestriction] = useState<'DOMAIN_NOT_ALLOWED' | null>(null);
  // 비밀번호 값은 서버 미노출 — 설정 여부만 받아 입력창 표시를 결정
  const [hasPassword, setHasPassword] = useState(false);
  // Remove unused 'today' variable
  // let today = new Date();
  const [password, setPassword] = useState("");
  const userImageUrls = invite?.roomUserImages ?? room?.users.map(u => u.photoUrl ?? Util.ANONYMOUS_PHOTO_URL) ?? [];

  useEffect(() => {
    const platform = UtilApp.getPlatform();
    setIsMobileBrowser(platform === 'android-browser' || platform === 'ios-browser');
    start();
  }, [invitationId])

  async function start() {
    setInProgress(true);
    try {
      let resp = await Fire.item.getRoomInviteInfo(invitationId || "");
      if (resp?.status === 'EXPIRED') {
        setIsExpired(true);
        return;
      }
      if (resp && resp.link) {
        setInvite(resp.link);
        setJoinRestriction(resp.joinRestriction ?? null);
        setHasPassword(resp.hasPassword ?? !!resp.link.password);
        const inv = resp.link;
        if (inv && My.isLoggedIn()) {
          const loadedRoom = await Fire.getRoom(inv.roomId);
          setRoom(loadedRoom);
        }
      }
    } catch (err) {
      // 존재하지 않는/삭제된 초대 링크는 functions 가 not-found 를 throw.
      // 정상적인 케이스이므로 에러 리포팅 대신 "찾을 수 없음" UI 로 폴백.
      console.info('[RoomInvitation] 초대 링크 조회 실패(무효/삭제된 링크):', err);
    } finally {
      setInProgress(false);
    }
  }

  async function joinRoom() {
    if (joinRestriction) return;
    if (!invite || !room || !My.isLoggedIn()) return;

    // 만료/비밀번호/도메인 정책 검증은 서버(joinRoomViaInvite)가 수행
    setInProgress(true);
    let res: JoinRoomViaInviteResponse;
    try {
      res = await Fire.joinRoom(invite.id, password || null);
    } catch (e) {
      console.error("joinRoomViaInvite failed:", e);
      res = { status: 'ERROR' };
    }
    setInProgress(false);

    switch (res.status) {
      case 'SUCCESS':
      case 'ALREADY_MEMBER':
        // 해당 방의 워크스페이스로 좌측 메뉴 전환
        if (room.workspaceId) {
          setActiveWorkspaceId(room.workspaceId);
        }
        history.replace(`/channel/${room.id}`);
        break;
      case 'PASSWORD_REQUIRED':
      case 'PASSWORD_INCORRECT':
        setPasswordError(t`Password is incorrect.`);
        break;
      case 'EXPIRED':
        setInvite(null);
        setIsExpired(true);
        break;
      case 'NOT_ALLOWED':
        setJoinRestriction('DOMAIN_NOT_ALLOWED');
        break;
      default:
        // NOT_FOUND / ERROR — 초대를 찾을 수 없음 카드로 전환
        setInvite(null);
        break;
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background md:rounded-panel md:border md:border-border items-center justify-center p-4 relative">



      {/* Loading State */}
      {!invite && inProgress && (
        <div className="text-center mt-20">
          <p className="text-muted-foreground mb-4"><Trans>Fetching the invitation...</Trans></p>
          <Player autoplay keepLastFrame src="assets/splash.json" style={{ height: '160px', width: '160px' }} />
        </div>
      )}

      {/* Invitation Content */}
      {invite && (
        <Card className="w-full max-w-md mx-auto shadow-lg border-none">
          <CardHeader className="text-center pb-2">
            <p className="text-lg font-medium text-foreground"><Trans>Invitation link by {invite.creator.name}</Trans></p>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4 pt-4">
            {roomImage && (
              <img src={roomImage} alt="Room" className="w-24 h-24 rounded-2xl object-cover shadow-sm" />
            )}

            <div className="text-center space-y-2">
              {roomName && <h2 className="text-2xl font-bold text-foreground"># {roomName}</h2>}
              {roomDescription && <p className="text-card-foreground">{roomDescription}</p>}
            </div>

            {getMemberList(6, userImageUrls)}

            <div className="w-full pt-4">
              {getActionButton()}
            </div>

            {getPromotionText()}
          </CardContent>
        </Card>
      )}

      {/* Error/Expired States */}
      {(!inProgress && !invite) && (
        <div className="text-center space-y-6 max-w-sm mx-auto mt-12 bg-background p-8 rounded-xl shadow-sm">
          {isExpired ? (
            <div className="flex flex-col items-center gap-4">
              <XCircle className="w-16 h-16 text-destructive" />
              <h1 className="text-xl font-bold text-foreground"><Trans>Link Expired</Trans></h1>
              <p className="text-card-foreground"><Trans>This invitation link has expired.</Trans></p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="w-16 h-16 text-muted-foreground" />
              <h4 className="text-xl font-medium text-foreground"><Trans>Invitation cannot be found.</Trans></h4>
              <Button variant="outline" onClick={() => history.push('/')}>
                <Trans>Back</Trans>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  function getMemberList(max: number, userImageUrls: string[]) {
    if (userImageUrls.length === 0) return null;

    return (
      <div className="bg-muted/50 rounded-xl p-4 w-full text-center">
        <div className="flex flex-wrap justify-center gap-2 mb-2">
          {userImageUrls.slice(0, 10).map((photoUrl, idx) => (
            <Avatar key={`${photoUrl}-${idx}`} className="w-8 h-8 ring-2 ring-white">
              <ProfileImg url={photoUrl} className="" />
            </Avatar>
          ))}
        </div>
        <p className="text-sm text-muted-foreground font-medium"><Trans>{userImageUrls.length} members</Trans></p>
      </div>
    );
  }

  function getActionButton() {
    if (My.isLoggedIn())
      return getJoinButton();
    else {
      if (isMobileBrowser) {
        const mainDomain = import.meta.env.VITE_MAIN_DOMAIN || 'app.nextpod.jp';
        const urlScheme = mainDomain.startsWith('devapp') ? 'nextpod-dev' : 'nextpod';
        let openWithAppLink = `${urlScheme}://${mainDomain}/invitation/${invitationId}`;
        return (
          <div className="text-center space-y-4 p-4 bg-blue-50 rounded-xl">
            <h6 className="font-semibold text-blue-900"><Trans>You're on a mobile browser.</Trans></h6>
            <div className="space-y-2">
              <p className="text-sm text-blue-800"><Trans>Install the mobile app</Trans></p>
              <div className="flex justify-center gap-2">
                <a href="https://play.google.com/store/apps/details?id=jp.nextpod.app" target="_blank" rel="noopener noreferrer">
                  <img src="/assets/img/badge-google.png" className="h-9" alt="Get it on Google Play" />
                </a>
                <a href="https://apps.apple.com/app/nextpod/id6752560289" target="_blank" rel="noopener noreferrer">
                  <img src="/assets/img/badge-apple.png" className="h-9" alt="Download on the App Store" />
                </a>
              </div>
            </div>

            <div className="pt-2">
              <p className="text-sm text-blue-800 mb-2"><Trans>Already installed?</Trans></p>
              <a href={openWithAppLink} className="inline-block px-4 py-2 bg-blue-600 text-on-primary-text rounded-full text-sm font-medium hover:bg-blue-700 transition-colors">
                <Trans>Open with NEXTPOD app</Trans>
              </a>
            </div>

            <p className="text-xs text-blue-600 pt-2"><Trans>You can also use NEXTPOD on desktop at https://app.nextpod.jp</Trans></p>
          </div>
        );
      } else {
        return getLoginButton()
      }
    }
  }

  function getJoinButton() {
    if (room) {
      if (room.userIds.includes(My.user.id)) {
        return (
          <div className="space-y-3 w-full">
            <p className="text-sm text-center text-card-foreground"><Trans>You have already joined #{`${room.name}`}.</Trans></p>
            <Button onClick={() => history.replace(`/channel/${room.id}`)} className="w-full">
              <Trans>Go to channel</Trans>
            </Button>
          </div>
        );
      } else if (joinRestriction === 'DOMAIN_NOT_ALLOWED') {
        // 도메인 제한 워크스페이스: 비허용 도메인 계정은 참가 불가 사전 안내
        return (
          <div className="space-y-3 w-full">
            <div className="flex flex-col items-center gap-2 rounded-xl bg-muted/50 p-4 text-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
              <p className="text-sm text-card-foreground">
                <Trans>This workspace only allows members with approved email domains. You cannot join this channel with your current account.</Trans>
              </p>
            </div>
            <Button variant="outline" onClick={() => history.push('/')} className="w-full">
              <Trans>Back</Trans>
            </Button>
          </div>
        );
      } else {
        return (
          <div className="space-y-3 w-full">
            {hasPassword && (
              <div className="space-y-1">
                <Input
                  type="password"
                  placeholder={t`Enter channel password.`}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                  className={passwordError ? "border-red-500 focus-visible:ring-red-500" : ""}
                />
                {passwordError && <p className="text-xs text-destructive ml-1">{passwordError}</p>}
              </div>
            )}
            <Button disabled={inProgress} onClick={joinRoom} data-cy="join" className="w-full bg-primary hover:bg-primary/90 text-on-primary-text">
              <Trans>Join the channel</Trans>
            </Button>
          </div>
        );
      }
    }
    return null;
  }

  function getLoginButton() {
    let path = `/invitation/${invitationId}`;
    return (
      <div className="space-y-3 w-full">
        <p className="text-sm text-center text-card-foreground"><Trans>You need to sign up/login to join the channel.</Trans></p>
        <Button
          onClick={() => history.push(`/login?redirect=${path}`)}
          data-cy="signup"
          variant="outline"
          className="w-full"
        >
          <Trans>Sign up/login</Trans>
        </Button>
      </div>
    );
  }

  function getPromotionText() {
    if (!My.isLoggedIn()) {
      return (
        <div className="w-full">
          <Separator className="my-6" />
          <div className="text-center space-y-3">
            <div className="flex justify-center">
              <img src="assets/icon/favicon.png" alt="Logo" className="w-10 h-10 rounded-lg shadow-sm" />
            </div>
            <h5 className="font-semibold text-foreground leading-tight">
              <Trans>Easy communication with NEXTPOD<br /> No matter where you are.</Trans>
            </h5>
            <p className="text-sm text-card-foreground leading-relaxed text-justify px-2">
              <Trans>NEXTPOD is a cloud based communication service that is designed to simplify remote collaboration between
                teams across and within organizations. You can create private channels and invite people for collaboration
                using instant messaging, voice and screen recordings, file sharing and real time voice calls.</Trans>
            </p>
          </div>
        </div>
      );
    }
    return null; // Return null if logged in
  }
}

export default RoomInvitationPage;